import {useEffect,useLayoutEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';

const palette=[
  '#c99a45','#e1b866','#f3e7d0','#ffffff','#1b100b','#080807',
  '#8b2635','#b55b65','#dc9d9b','#8b5e3c','#b68a62','#607d63',
  '#a9b99c','#547b87','#7081a8','#756184','#c8a6bc','#d68f54'
];
const colorVariables=[
  '--admin-surface','--admin-surface-raised','--admin-border',
  '--admin-muted','--admin-text','--admin-accent-strong'
];

function SafeColorPicker({value,onChange,label}) {
  const [open,setOpen]=useState(false);
  const [draft,setDraft]=useState(value || '#c99a45');
  const [position,setPosition]=useState(null);
  const trigger=useRef(null);
  const popover=useRef(null);

  useEffect(()=> {
    setDraft(value || '#c99a45');
  },[value]);

  useLayoutEffect(()=> {
    if (!open) return;
    const updatePosition=()=> {
      const button=trigger.current;
      if (!button) return;
      const bounds=button.getBoundingClientRect();
      const width=Math.min(270,window.innerWidth - 24);
      const height=popover.current?.getBoundingClientRect().height || 390;
      const below=window.innerHeight - bounds.bottom;
      const above=bounds.top;
      const placeAbove=below < height + 12 && above > below;
      setPosition({
        left: Math.max(12,Math.min(bounds.left,window.innerWidth - width - 12)),
        top: placeAbove
          ? Math.max(12,bounds.top - height - 8)
          : Math.min(bounds.bottom + 8,window.innerHeight - height - 12),
        width
      });
    };
    updatePosition();
    window.addEventListener('resize',updatePosition);
    window.addEventListener('scroll',updatePosition,true);
    return ()=> {
      window.removeEventListener('resize',updatePosition);
      window.removeEventListener('scroll',updatePosition,true);
    };
  },[open]);

  useEffect(()=> {
    if (!open) return;
    const closeOutside=(event)=> {
      if (!trigger.current?.contains(event.target) && !popover.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const closeOnEscape=(event)=> {
      if (event.key==='Escape') setOpen(false);
    };
    document.addEventListener('pointerdown',closeOutside);
    document.addEventListener('keydown',closeOnEscape);
    return ()=> {
      document.removeEventListener('pointerdown',closeOutside);
      document.removeEventListener('keydown',closeOnEscape);
    };
  },[open]);

  const applyHex=()=> {
    const normalized=`#${draft.replace(/^#/,'')}`;
    if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
      onChange(normalized.toLowerCase());
      setOpen(false);
    }
  };

  const themeStyles=trigger.current
    ? Object.fromEntries(colorVariables.map((name)=> [
      name,getComputedStyle(trigger.current).getPropertyValue(name)
    ]))
    : {};

  return (
    <div className="safe-color-picker">
      <button
        ref={trigger}
        type="button"
        className="safe-color-trigger"
        style={{backgroundColor: /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#c99a45'}}
        onClick={()=> setOpen((current)=> !current)}
        aria-label={`${label}: choose display color`}
        aria-expanded={open}
        title="Choose a color"
      />
      {open && createPortal(
        <div
          ref={popover}
          className="safe-color-popover"
          style={{...themeStyles,...position}}
        >
          <span className="safe-color-title">Choose a color</span>
          <div className="safe-color-palette">
            {palette.map((hex)=> (
              <button
                type="button"
                key={hex}
                className={`safe-color-option ${value?.toLowerCase()===hex ? 'selected' : ''}`}
                style={{backgroundColor: hex}}
                onClick={()=> {onChange(hex);setOpen(false);}}
                aria-label={`Select ${hex}`}
                title={hex}
              />
            ))}
          </div>
          <label className="safe-color-hex">
            Hex color
            <input
              type="text"
              value={draft}
              onChange={(event)=> setDraft(event.target.value)}
              onKeyDown={(event)=> {
                if (event.key==='Enter') {
                  event.preventDefault();
                  applyHex();
                }
              }}
              maxLength={7}
              spellCheck={false}
              placeholder="#c99a45"
            />
          </label>
          <button type="button" className="safe-color-apply" onClick={applyHex}>
            Apply color
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SafeColorPicker;